import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { toCmdbObjectId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { getCmdbAnalyticAttributesFromSchema } from '@atlassian/jira-servicedesk-cmdb-analytics/src/index.tsx';
import { createSelector } from '@atlassian/react-sweet-state';
import type { InsightObjectViewState } from '../../common/types/index.tsx';

export const getObjectDetails = (state: InsightObjectViewState) => state.objectDetails;

export const getObjectTypeId = (state: InsightObjectViewState) =>
	getObjectDetails(state)?.objectType?.id ?? '';

export const getObjectId = (state: InsightObjectViewState) =>
	getObjectDetails(state)?.id ?? toCmdbObjectId('');

export const getObjectConfiguration = (state: InsightObjectViewState) => state.objectConfiguration;

export const getObjectSchemaId = (state: InsightObjectViewState) =>
	getObjectConfiguration(state)?.objectSchema.id;

export const getObjectSchemaName = (state: InsightObjectViewState) =>
	getObjectConfiguration(state)?.objectSchema.name;

export const getHasDeveloperRoleForObjectType = (state: InsightObjectViewState) =>
	getObjectConfiguration(state)?.insightDeveloperForObjectType ?? false;

export const getHasManagerRoleForObjectType = (state: InsightObjectViewState) =>
	getObjectConfiguration(state)?.insightManagerForObjectType ?? false;

export const getAttributes = (state: InsightObjectViewState) =>
	getObjectDetails(state)?.attributes ?? [];

export const getLabelAttribute = createSelector(
	(state: InsightObjectViewState) => getAttributes(state),
	(attributes) => {
		const labelAttribute = attributes.find((attr) => attr.objectTypeAttribute.label === true);
		if (labelAttribute && labelAttribute.type === 'default') {
			return labelAttribute;
		}

		// https://hello.jira.atlassian.cloud/browse/FLY-1716
		// Temporarily putting this here to better understand why the label is sometimes missing.
		log.safeErrorWithoutCustomerData(
			'servicedesk.insight-object.object-details-store.selectors.missing-label-attribute',
			`attributesLength: ${attributes.length}, labelSet: ${!!labelAttribute}, labelAttributeType: ${labelAttribute?.type}`,
		);

		throw new Error('Label should be always be a default text attribute');
	},
);

export const getEditableAttributesTypes = (state: InsightObjectViewState) =>
	getObjectDetails(state)?.editableAttributeTypes ?? [];

export const getAttribute = createSelector(
	(state: InsightObjectViewState, objectTypeAttributeId: string) => {
		const attributes = getAttributes(state);
		return attributes.find((attr) => attr.objectTypeAttribute.id === objectTypeAttributeId);
	},
	(attribute) => attribute,
);

// TODO this can be be potentially deleted / replaced / renamed depending if we need to do slightly different things in SR versus Insight in the future post API unification
export const getIsRestrictedSchema = (state: InsightObjectViewState) =>
	getObjectConfiguration(state)?.restrictedObjectSchemaIds.includes(
		Number(getObjectSchemaId(state)),
	) ?? false;

export const getCmdbAnalyticAttributes = (state: InsightObjectViewState) => {
	const objectConfiguration = getObjectConfiguration(state);
	if (!objectConfiguration) {
		// We shouldn't return analytics attributes if no object configuration
		return null;
	}
	return getCmdbAnalyticAttributesFromSchema(objectConfiguration.objectSchema);
};
